/** `https://`, `http://`, `ftp://` — a hierarchical scheme, authority and all. */
const ABSOLUTE = /^[a-z][a-z0-9+.-]*:\/\//i;

/**
 * `mailto:`, `tel:`, `sms:` — a scheme with an opaque body. Dots are deliberately excluded from
 * the scheme so `example.com:8080` reads as a host with a port rather than a scheme of its own.
 */
const OPAQUE_SCHEME = /^[a-z][a-z0-9+-]*:/i;

/**
 * Completes an external link typed without a scheme, so `example.com` can be entered as-is.
 *
 * The completed value is what the form then holds, which is what gets validated and saved: the
 * section schema checks the URL against the `uri` format, so anything that is still not a URL once
 * completed — `not a url` — fails exactly as before. A value that already carries a scheme is
 * returned untouched, so nothing ever gains a second one.
 */
export function withScheme(url: string): string {
  const trimmed = url.trim();
  if (!trimmed || ABSOLUTE.test(trimmed) || OPAQUE_SCHEME.test(trimmed)) return trimmed;

  // Protocol-relative: the authority is already there, only the scheme is missing.
  if (trimmed.startsWith('//')) return `https:${trimmed}`;

  // A path is not a host. Completing `/about` would invent an origin for it, so it is left to fail
  // validation with the "enter a full URL" hint it deserves — internal links come from the page
  // picker, which stores them on read-only rows the form never validates as URLs.
  if (trimmed.startsWith('/')) return trimmed;

  return `https://${trimmed}`;
}
